import axios from "axios";

import * as api from "../../types";
import { FreebieRuleStatus, FreebieRuleType } from "../../../utils/enums";

export interface ICreateFreebieRuleParameter {
    token: string;
    name: string;
    type: FreebieRuleType;
    status: FreebieRuleStatus;
    priority: number;
    minCartValue: number | null;
    discountCode: string | null;
    quantity: number;
    startsAt: Date | null;
    endsAt: Date | null;
    triggerVariantIds: string[];
    freebieVariantIds: string[];
}

export const createFreebieRule = async (
    parameters: ICreateFreebieRuleParameter,
): Promise<api.IFreebieRule> => {
    const { token, ...body } = parameters;
    const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/freebie-rules`,
        body,
        {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                "Admin-Request": "true",
            },
        },
    );
    return transform(data);
};

export interface IUpdateFreebieRuleParameter {
    id: string;
    token: string;
    name?: string;
    type?: FreebieRuleType;
    status?: FreebieRuleStatus;
    priority?: number;
    minCartValue?: number | null;
    discountCode?: string | null;
    quantity?: number;
    startsAt?: Date | null;
    endsAt?: Date | null;
    triggerVariantIds?: string[];
    freebieVariantIds?: string[];
}

export const updateFreebieRule = async (
    parameters: IUpdateFreebieRuleParameter,
): Promise<api.IFreebieRule> => {
    const { id, token, ...body } = parameters;
    const { data } = await axios.patch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/freebie-rules/${id}`,
        body,
        {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                "Admin-Request": "true",
            },
        },
    );
    return transform(data);
};

export interface IDeleteFreebieRuleParameter {
    token: string;
    id: string;
}

export const deleteFreebieRule = async (
    parameters: IDeleteFreebieRuleParameter,
): Promise<api.IFreebieRule> => {
    const { id, token } = parameters;
    const { data } = await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/freebie-rules/${id}`,
        {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                "Admin-Request": "true",
            },
        },
    );
    return transform(data);
};

export interface IGetAllFreebieRulesProps {
    token: string;
    limit: number;
    cursor: string | null;
    direction: string;
    search?: string;
    status?: number;
    type?: number;
    sortBy?: string;
    sortOrder?: string;
}

export const fetchAllFreebieRules = async (
    parameters: IGetAllFreebieRulesProps,
): Promise<api.IExternalListPage<api.IFreebieRule>> => {
    const { token, ...body } = parameters;
    const params = new URLSearchParams([
        ["limit", body.limit.toString()],
        ["direction", body.direction],
    ]);

    if (body.cursor) {
        params.append("cursor", body.cursor);
    }
    if (body.search) {
        params.append("search", body.search);
    }
    if (body.status !== undefined) {
        params.append("status", body.status.toString());
    }
    if (body.type !== undefined) {
        params.append("type", body.type.toString());
    }
    if (body.sortBy) {
        params.append("sortBy", body.sortBy);
    }
    if (body.sortOrder) {
        params.append("sortOrder", body.sortOrder);
    }
    const { data } = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/freebie-rules?${params}`,
        {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                "Admin-Request": "true",
            },
        },
    );
    return { ...data, records: data.records.map(transform) };
};

export const fetchFreebieRuleById = async (
    token: string,
    freebieRuleId: string | undefined,
): Promise<api.IFreebieRule> => {
    const { data } = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/freebie-rules/${freebieRuleId}`,
        {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
                "Admin-Request": "true",
            },
        },
    );
    return transform(data);
};

const transform = (data: api.IFreebieRule): api.IFreebieRule => {
    const { startsAt, endsAt, createdAt, updatedAt, ...rest } = data;
    return {
        ...rest,
        startsAt: startsAt ? new Date(startsAt) : null,
        endsAt: endsAt ? new Date(endsAt) : null,
        createdAt: new Date(createdAt),
        updatedAt: new Date(updatedAt),
    };
};
